import { Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CompareArrows, FileDownloadOutlined } from '@mui/icons-material';
import { ComparisonCard } from '@/components/molecules/comparisonCard';
import { Flex } from '@/styles/globalStyles';
import { MunicipalityComparative, MunicipalitiesComparative } from '@/graphql/types/municipality';
import { DimensionVariable } from '@/graphql/types/dimensions';
import { OpportunityAndRiskCard } from '@/components/molecules/cards/opportunityAndRisk';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { StyledPaper } from './styles';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { useState } from 'react';
import { MunicipalityActions } from '@/components/molecules/municipalityActions';
import { GenericChart } from './chart';
import { GenericComposition } from './composition';
import type { Municipality } from '@/components/molecules/dimensionVariableComparative';
import { DimensionVariableComparative } from '@/components/molecules/dimensionVariableComparative';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { Text } from '@/components/atoms/text';
import { VariableInfoModal } from '@/components/molecules/variable/modal';
import type { MenuButtonOption } from '@/components/molecules/button/menu';
import { DownloadUsage } from '@/graphql/types/downloads';
import { ClassificationOpportunityAndRisk } from '@/types/classification';
import { ComparativeMunicipalitiesMap } from '@/components/molecules/comparativeMunicipalitiesMap';

interface GenericTabProps {
  dimensionVariablesComparative: {
    municipalities: Municipality[];
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  };
  municipalityVsStateData: {
    data: MunicipalityComparative[];
    onInsight: () => void;
  };
  comparativeData: {
    data: MunicipalitiesComparative[];
    onInsight: () => void;
    onCompare: () => void;
    downloadOptions?: MenuButtonOption[];
    isGeneratingPDF: boolean;
  };
  tableData: {
    opportunityVariables: DimensionVariable[];
    riskVariables: DimensionVariable[];
  };
  loadingData?: boolean;
  loadingPDF?: boolean;
  loadingXLSX?: boolean;
  downloadUsage?: DownloadUsage;
  limitReachedMessage?: string;
  comparisonMapDimensionId: number;
  comparisonMapBigqueryId?: string;
  comparisonMapDimensionName?: string;
}

export const GenericTab = ({
  dimensionVariablesComparative,
  municipalityVsStateData,
  comparativeData,
  tableData,
  loadingData,
  loadingPDF,
  loadingXLSX,
  downloadUsage,
  limitReachedMessage,
  comparisonMapDimensionId,
  comparisonMapBigqueryId,
  comparisonMapDimensionName,
}: GenericTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState<DimensionVariable | undefined>();

  const comparativeOpportunityData = comparativeData?.data.map((item) => {
    const dimensionData = Object.values(item.dimensions)[0];
    // Fallback to a default value if classification is undefined

    return {
      title: item.municipality.name_municipality,
      classification: (dimensionData?.classification_opportunity ||
        item.classification.opportunity) as ClassificationOpportunityAndRisk,
      data: {
        note: dimensionData?.municipality_opportunity || item.score.opportunity || '0',
        color: dimensionData?.intensity_color_opportunity || '#116938',
      },
    };
  });

  const comparativeRiskData = comparativeData?.data.map((item) => {
    const dimensionData = Object.values(item.dimensions)[0];

    return {
      title: item.municipality.name_municipality,
      classification: (dimensionData?.classification_risk ||
        item.classification.risk) as ClassificationOpportunityAndRisk,
      data: {
        note: dimensionData?.municipality_risk || item.score.risk || '0',
        color: dimensionData?.intensity_color_risk || '#fe9b33',
      },
    };
  });

  const opportunityData = municipalityVsStateData?.data
    .map((item) => {
      return [
        {
          title: item.municipality.name_municipality,
          noTooltip: true,
          classification: item.classification_opportunity,
          data: {
            note: item.municipality_opportunity,
            color: '#116938',
          },
        },
        {
          title: item.municipality.name_uf,
          noTooltip: true,
          classification: item.classification_opportunity,
          data: {
            note: item.state_average_opportunity,
            color: '#116938',
          },
        },
      ];
    })
    .flat();

  const riskData = municipalityVsStateData?.data
    .map((item) => {
      return [
        {
          title: item.municipality.name_municipality,
          noTooltip: true,
          classification: item.classification_risk,
          data: {
            note: item.municipality_risk,
            color: '#fe9b33',
          },
        },
        {
          title: item.municipality.name_uf,
          noTooltip: true,
          classification: item.classification_risk,
          data: {
            note: item.state_average_risk,
            color: '#fe9b33',
          },
        },
      ];
    })
    .flat();

  const GenericComparisonCard = () => {
    return (
      <ComparisonCard title={t('label.label.composition')}>
        <Flex align="flex-start" gap={3} fullWidth>
          <GenericComposition
            tableData={tableData}
            setOpenModal={setOpenModal}
            setModalData={setModalData}
            variant="opportunity"
          />

          <GenericComposition
            tableData={tableData}
            setOpenModal={setOpenModal}
            setModalData={setModalData}
            variant="risk"
          />
        </Flex>
      </ComparisonCard>
    );
  };

  return (
    <>
      {loadingData ? (
        <Skeleton animation="wave" height={260} width="100%" sx={{ transform: 'none' }} />
      ) : (
        <>
          <GenericComparisonCard />

          <ComparisonCard
            title={t('label.label.municipality_vs_state')}
            onInsight={() => municipalityVsStateData?.onInsight()}
            buttons={
              isMobile
                ? undefined
                : [
                    {
                      text: t('button.button.insights'),
                      icon: SparklesIcon,
                      variant: 'outlined',
                      onClick: () => municipalityVsStateData?.onInsight(),
                    },
                  ]
            }
          >
            <Flex gap={3} justify="space-between" align="stretch" fullWidth>
              <OpportunityAndRiskCard variant="opportunity">
                <GenericChart chartData={opportunityData} variant="opportunity" />
              </OpportunityAndRiskCard>

              <OpportunityAndRiskCard variant="risk">
                <GenericChart chartData={riskData} variant="risk" />
              </OpportunityAndRiskCard>
            </Flex>
          </ComparisonCard>

          {comparativeData && comparativeData.data.length > 0 ? (
            <ComparisonCard
              title={t('label.label.municipality_vs_municipality')}
              hasComparison={comparativeData?.data.length > 0}
              loadingPDF={loadingPDF}
              loadingXLSX={loadingXLSX}
              downloadUsage={downloadUsage}
              limitReachedMessage={limitReachedMessage}
              buttons={[
                {
                  text: t('button.button.insights'),
                  icon: SparklesIcon,
                  variant: 'outlined',
                  onClick: () => comparativeData?.onInsight(),
                },
                {
                  text: t('button.button.export'),
                  icon: FileDownloadOutlined,
                  variant: 'outlined',
                  loading: comparativeData?.isGeneratingPDF,
                  downloadOptions: comparativeData?.downloadOptions,
                },
                {
                  text: t('button.button.compare_municipality'),
                  icon: CompareArrows,
                  variant: 'contained',
                  onClick: () => comparativeData?.onCompare(),
                },
              ]}
            >
              <Flex gap={3} justify="space-between" align="stretch" fullWidth>
                <OpportunityAndRiskCard variant="opportunity">
                  <GenericChart chartData={comparativeOpportunityData} variant="opportunity" />
                </OpportunityAndRiskCard>

                <OpportunityAndRiskCard variant="risk">
                  <GenericChart chartData={comparativeRiskData} variant="risk" />
                </OpportunityAndRiskCard>
              </Flex>

              <Flex gap={3} direction="column" align="stretch" fullWidth>
                <OpportunityAndRiskCard variant="opportunity" padding={0}>
                  {dimensionVariablesComparative.opportunityVariables.length > 0 ? (
                    <DimensionVariableComparative
                      dimensionVariablesComparative={dimensionVariablesComparative}
                      variant="opportunity"
                    />
                  ) : (
                    <Text
                      variant="subtitle1"
                      color="text.disabled"
                      sx={{
                        width: '100%',
                        textAlign: 'center',
                        padding: theme.spacing(3),
                      }}
                    >
                      <SafeMarkdown text={t('label.label.no_opportunity_calculated')} />
                    </Text>
                  )}
                </OpportunityAndRiskCard>

                <OpportunityAndRiskCard variant="risk" padding={0}>
                  {dimensionVariablesComparative.riskVariables.length > 0 ? (
                    <DimensionVariableComparative
                      dimensionVariablesComparative={dimensionVariablesComparative}
                      variant="risk"
                    />
                  ) : (
                    <Text
                      variant="subtitle1"
                      color="text.disabled"
                      sx={{
                        width: '100%',
                        textAlign: 'center',
                        padding: theme.spacing(3),
                      }}
                    >
                      <SafeMarkdown text={t('label.label.no_risk_calculated')} />
                    </Text>
                  )}
                </OpportunityAndRiskCard>
              </Flex>

              <Flex align="stretch" fullWidth sx={{ marginTop: theme.spacing(3) }}>
                <ComparativeMunicipalitiesMap
                  data={comparativeData.data}
                  dimensionId={comparisonMapDimensionId}
                  selectedBigqueryId={comparisonMapBigqueryId}
                  selectedDimensionName={comparisonMapDimensionName}
                />
              </Flex>
            </ComparisonCard>
          ) : (
            <StyledPaper variant="outlined">
              <MunicipalityActions
                hasComparison={comparativeData?.data.length > 0}
                compareButton={{
                  onClick: () => comparativeData?.onCompare(),
                }}
                insightButton={{
                  onClick: () => comparativeData?.onInsight(),
                }}
                downloadOptions={comparativeData?.downloadOptions}
                loadingPDF={loadingPDF}
                loadingXLSX={loadingXLSX}
                downloadUsage={downloadUsage}
                limitReachedMessage={limitReachedMessage}
              />
            </StyledPaper>
          )}
        </>
      )}

      <Mobile>
        {openModal && (
          <VariableInfoModal
            title={modalData?.name ?? ''}
            description={modalData?.description ?? ''}
            source={modalData?.source ?? ''}
            open={openModal}
            onClose={() => setOpenModal(false)}
            timeSeries={modalData?.time_series}
          />
        )}
      </Mobile>
    </>
  );
};
